import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { StatusIcon } from "../components/StatusIcon";

// ── Types ──────────────────────────────────────────────────────────────────

type CatalogHealth = {
  status: "ok" | "stale" | "pending";
  last_updated: string | null;
  days_since: number | null;
  threshold_days: number;
  rows_total: number;
  rows_refreshed: number;
};

type SourceStat = {
  id: number;
  name: string;
  code: string;
  url: string | null;
  frequency: string;
  is_active: boolean;
  last_checked_at: string | null;
  last_successful_at: string | null;
  next_run: string | null;
  status: "ok" | "warning" | "pending" | "inactive";
  total: number;
  last_7d: number;
  last_30d: number;
  last_change_at: string | null;
};

// ── Helpers ────────────────────────────────────────────────────────────────
// v3 liquid glass: el color SOLO vive en el punto de estado (semántico).
// Números en tinta, tabulares; nada de números arcoíris ni bordes tintados.

const STATUS_META: Record<string, { label: string; dot: string }> = {
  ok:       { label: "Activa",    dot: "#16a34a" },
  warning:  { label: "Aviso",     dot: "#d97706" },
  pending:  { label: "Pendiente", dot: "#64748b" },
  inactive: { label: "Inactiva",  dot: "#94a3b8" },
};

/** Cápsula de estado tintada (color con presencia + icono animado). */
function StatusPill({ status }: { status: string }) {
  const sm = STATUS_META[status] ?? STATUS_META.inactive;
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      background: sm.dot + "1e", color: sm.dot,
      borderRadius: 999, padding: "3px 11px",
      fontSize: "0.72rem", fontWeight: 600, whiteSpace: "nowrap",
    }}>
      <StatusIcon status={status} size={9} />
      {sm.label}
    </span>
  );
}

const FREQ_LABEL: Record<string, string> = {
  daily:     "Diaria",
  weekly:    "Semanal",
  monthly:   "Mensual",
  quarterly: "Trimestral",
};

function fmtDatetime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" })
    + " " + d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("es-ES", { day: "2-digit", month: "short", year: "numeric" });
}

function timeAgo(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins  = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days  = Math.floor(diff / 86400000);
  if (mins < 60)  return `hace ${mins} min`;
  if (hours < 24) return `hace ${hours} h`;
  return `hace ${days} días`;
}


// ── Source Card ────────────────────────────────────────────────────────────
function SourceCard({ s, onClick }: { s: SourceStat; onClick: () => void }) {
  const [hov, setHov] = useState(false);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: "var(--clr-surface)",
        border: "1px solid var(--clr-border)",
        borderRadius: 18,
        padding: "1.15rem 1.25rem",
        backdropFilter: "var(--clr-blur)",
        WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: hov ? "var(--clr-shadow)" : "var(--clr-shadow-sm)",
        transform: hov ? "translateY(-2px)" : "none",
        transition: "transform 0.25s cubic-bezier(.25,.6,.3,1), box-shadow 0.25s",
        cursor: "pointer",
        display: "flex",
        flexDirection: "column",
        gap: 14,
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {/* Nombre + estado */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 600, fontSize: "0.9rem", color: "var(--clr-text)", letterSpacing: "-0.01em",
            whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
          }}>{s.name}</div>
          <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 3 }}>
            {FREQ_LABEL[s.frequency] ?? s.frequency}
            {s.last_change_at && <> · último cambio {timeAgo(s.last_change_at)}</>}
          </div>
        </div>
        <div style={{ flexShrink: 0, paddingTop: 1 }}>
          <StatusPill status={s.status} />
        </div>
      </div>

      {/* Métricas: franja con separadores finos (números en tinta) */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", borderTop: "1px solid var(--clr-border2)" }}>
        {[
          { label: "7 días",  value: s.last_7d },
          { label: "30 días", value: s.last_30d },
          { label: "Total",   value: s.total },
        ].map(({ label, value }, i) => (
          <div key={label} style={{
            paddingTop: 12,
            paddingLeft: i > 0 ? 14 : 0,
            borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none",
          }}>
            <div style={{ fontSize: "1.25rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
              {value.toLocaleString("es-ES")}
            </div>
            <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>{label}</div>
          </div>
        ))}
      </div>

      {/* Meta */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", fontSize: "0.72rem", color: "var(--clr-text4)" }}>
        <span>Últ. actualización</span>
        <span style={{ color: s.last_successful_at ? "var(--clr-text3)" : "#b45309", fontWeight: 500 }}>
          {s.last_successful_at ? fmtDate(s.last_successful_at) : "Sin datos"}
        </span>
      </div>
    </div>
  );
}

// ── Detail Modal ───────────────────────────────────────────────────────────
function SourceDetailModal({ s, onClose }: { s: SourceStat; onClose: () => void }) {

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(11,18,21,0.4)",
        zIndex: 400,
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "1rem",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--clr-modal-solid)",
          borderRadius: 22,
          width: "100%", maxWidth: 520,
          boxShadow: "0 24px 80px -24px rgba(0,0,0,0.45)",
          border: "1px solid var(--clr-border)",
          overflow: "hidden",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {/* Header */}
        <div style={{
          padding: "1.25rem 1.5rem",
          borderBottom: "1px solid var(--clr-border2)",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <span style={{ fontWeight: 600, fontSize: "1rem", letterSpacing: "-0.01em", color: "var(--clr-text)" }}>{s.name}</span>
              <StatusPill status={s.status} />
            </div>
            <div style={{ fontSize: "0.74rem", color: "var(--clr-text3)", marginTop: 5 }}>
              {FREQ_LABEL[s.frequency] ?? s.frequency}
            </div>
            {s.url && (
              <div style={{ fontSize: "0.68rem", color: "var(--clr-text4)", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 360 }}>
                {s.url}
              </div>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar"
            style={{
              width: 30, height: 30, borderRadius: 15,
              background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)",
              color: "var(--clr-text3)", cursor: "pointer", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "0.8rem", lineHeight: 1,
            }}
          >✕</button>
        </div>

        {/* Body */}
        <div style={{ padding: "1.35rem 1.5rem", display: "flex", flexDirection: "column", gap: 20 }}>
          {/* Cambios detectados — franja con separadores, números en tinta */}
          <div>
            <div style={{ fontSize: "0.66rem", textTransform: "uppercase", letterSpacing: 1, color: "var(--clr-text4)", marginBottom: 10 }}>
              Cambios detectados
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr" }}>
              {[
                { label: "7 días",  value: s.last_7d },
                { label: "30 días", value: s.last_30d },
                { label: "Total",   value: s.total },
              ].map(({ label, value }, i) => (
                <div key={label} style={{ paddingLeft: i > 0 ? 16 : 0, borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none" }}>
                  <div style={{ fontSize: "1.6rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", lineHeight: 1 }}>
                    {value.toLocaleString("es-ES")}
                  </div>
                  <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", marginTop: 5, textTransform: "uppercase", letterSpacing: 0.6 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Timestamps */}
          <div style={{ borderTop: "1px solid var(--clr-border2)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 9 }}>
            {[
              { label: "Última comprobación",           value: fmtDatetime(s.last_checked_at) },
              { label: "Última actualización correcta", value: fmtDatetime(s.last_successful_at) },
              { label: "Último cambio detectado",       value: fmtDatetime(s.last_change_at) },
              { label: "Próxima ejecución",             value: s.next_run ?? "—" },
            ].map(({ label, value }) => (
              <div key={label} style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: "0.75rem", color: "var(--clr-text4)", flexShrink: 0 }}>{label}</span>
                <span style={{ fontSize: "0.78rem", fontWeight: 500, color: "var(--clr-text2)", textAlign: "right" }}>{value}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "0.9rem 1.5rem", borderTop: "1px solid var(--clr-border2)", display: "flex", justifyContent: "flex-end" }}>
          <button
            onClick={onClose}
            style={{
              background: "var(--clr-modal-solid2)", border: "1px solid var(--clr-border2)",
              borderRadius: 999, padding: "0.5rem 1.3rem",
              fontSize: "0.8rem", fontWeight: 600, color: "var(--clr-text2)",
              cursor: "pointer",
            }}
          >Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────

type Filter = "all" | "ok" | "warning" | "inactive";

type SystemStatus = {
  overall: "ok" | "degraded";
  sources: { total: number; fresh: number; incident: number; fresh_pct: number | null };
  reliability_pct_30d: number | null;
  probes_30d: number;
  latency_target_hours: number;
};

type LatencyResp = { alerting: { p50_minutes: number | null }[] };

// Mediana de las p50 de aviso por fuente (min). null si no hay datos aún.
function medianAlertingMinutes(l: LatencyResp | null): number | null {
  if (!l) return null;
  const xs = l.alerting.map(a => a.p50_minutes).filter((v): v is number => v != null).sort((a, b) => a - b);
  if (!xs.length) return null;
  const mid = Math.floor(xs.length / 2);
  return xs.length % 2 ? xs[mid] : (xs[mid - 1] + xs[mid]) / 2;
}

export default function SuperAdminDashboardFuentesPage() {
  const [sources, setSources]   = useState<SourceStat[]>([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState("");
  const [filter, setFilter]     = useState<Filter>("all");
  const [selected, setSelected] = useState<SourceStat | null>(null);
  const [search, setSearch]     = useState("");
  const [catalog, setCatalog]   = useState<CatalogHealth | null>(null);
  const [status, setStatus]     = useState<SystemStatus | null>(null);
  const [latency, setLatency]   = useState<LatencyResp | null>(null);

  useEffect(() => {
    api.get<SourceStat[]>("/dashboard/sources-stats")
      .then(d => setSources(d))
      .catch(() => setError("No se pudieron cargar las fuentes."))
      .finally(() => setLoading(false));
    // El catálogo CIMA no es una fuente: si deja de refrescarse servimos
    // `is_comercializado` muerto sin que nada lo delate. Tarjeta aparte.
    api.get<CatalogHealth>("/dashboard/catalog-health").then(setCatalog).catch(() => {});
    // Estado consolidado (semáforo + frescura + fiabilidad) y latencia de aviso.
    api.get<SystemStatus>("/dashboard/system-status").then(setStatus).catch(() => {});
    api.get<LatencyResp>("/dashboard/detection-latency").then(setLatency).catch(() => {});
  }, []);

  const filtered = sources.filter(s => {
    const matchFilter =
      filter === "all" ? true :
      filter === "ok" ? s.status === "ok" :
      filter === "warning" ? (s.status === "warning" || s.status === "pending") :
      s.status === "inactive";
    const matchSearch = search === "" ||
      s.name.toLowerCase().includes(search.toLowerCase()) ||
      s.code.toLowerCase().includes(search.toLowerCase());
    return matchFilter && matchSearch;
  });

  const sc = sources.reduce((a, s) => {
    a[s.status] = (a[s.status] ?? 0) + 1;
    return a;
  }, {} as Record<string, number>);

  const totalChanges30d = sources.reduce((a, s) => a + s.last_30d, 0);
  const totalChanges7d  = sources.reduce((a, s) => a + s.last_7d,  0);

  if (loading) return (
    <div style={{ padding: "2.5rem", display: "flex", alignItems: "center", justifyContent: "center", minHeight: 300 }}>
      <div style={{ color: "var(--clr-text3)", fontSize: "0.85rem" }}>Cargando fuentes…</div>
    </div>
  );
  if (error) return (
    <div style={{ padding: "2rem", color: "#ef4444", fontSize: "0.85rem" }}>{error}</div>
  );

  return (
    <div style={{ padding: "1.75rem 2rem 2.5rem", maxWidth: 1400, margin: "0 auto", fontVariantNumeric: "tabular-nums" }}>

      {/* Header */}
      <div style={{ marginBottom: "1.5rem" }}>
        <h1 style={{ fontSize: "1.4rem", fontWeight: 700, letterSpacing: "-0.02em", color: "var(--clr-text)", margin: 0, marginBottom: 4 }}>
          Estado del sistema
        </h1>
        <p style={{ fontSize: "0.82rem", color: "var(--clr-text3)", margin: 0 }}>
          Salud del servicio, frescura de fuentes y cumplimiento del aviso.
        </p>
      </div>

      {/* Cabecera de estado consolidada: semáforo + KPIs (fuentes al día,
          fiabilidad de scrapers, latencia de aviso vs objetivo). */}
      {status && (() => {
        const ok = status.overall === "ok";
        const barColor = ok ? "#10b981" : "#f59e0b";
        const med = medianAlertingMinutes(latency);
        const tile = (label: string, value: string, sub?: string, color?: string) => (
          <div style={{
            flex: 1, minWidth: 150, background: "var(--clr-surface)",
            border: "1px solid var(--clr-border)", borderRadius: 16, padding: "0.9rem 1.1rem",
            backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)", boxShadow: "var(--clr-shadow-sm)",
          }}>
            <div style={{ fontSize: "0.62rem", color: "var(--clr-text4)", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.8, marginBottom: 6 }}>{label}</div>
            <div style={{ fontSize: "1.5rem", fontWeight: 700, letterSpacing: "-0.02em", color: color ?? "var(--clr-text)", lineHeight: 1 }}>{value}</div>
            {sub && <div style={{ fontSize: "0.7rem", color: "var(--clr-text4)", marginTop: 5 }}>{sub}</div>}
          </div>
        );
        return (
          <div style={{ marginBottom: "1.5rem" }}>
            {/* Semáforo global */}
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
              background: barColor + "14", border: `1px solid ${barColor}44`,
              borderRadius: 14, padding: "0.7rem 1.1rem",
            }}>
              <StatusIcon status={ok ? "ok" : "warning"} size={12} />
              <span style={{ fontWeight: 700, color: "var(--clr-text)", fontSize: "0.95rem" }}>
                {ok ? "Operativo" : "Degradado"}
              </span>
              <span style={{ color: "var(--clr-text3)", fontSize: "0.8rem" }}>
                {ok ? "Todas las fuentes al día." : `${status.sources.incident} fuente(s) con incidencia.`}
              </span>
            </div>
            {/* KPIs */}
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              {tile("Fuentes al día",
                    `${status.sources.fresh}/${status.sources.total}`,
                    status.sources.fresh_pct != null ? `${status.sources.fresh_pct}% frescas` : undefined,
                    status.sources.incident ? "#f59e0b" : "#10b981")}
              {tile("Fiabilidad scrapers (30d)",
                    status.reliability_pct_30d != null ? `${status.reliability_pct_30d}%` : "—",
                    status.probes_30d ? `${status.probes_30d} sondeos` : "sin datos aún")}
              {tile("Tiempo de aviso (mediana)",
                    med != null ? (med < 60 ? `${Math.round(med)} min` : `${(med / 60).toFixed(1)} h`) : "—",
                    med != null ? `objetivo ≤ ${status.latency_target_hours} h` : "sin datos aún",
                    med != null && med / 60 <= status.latency_target_hours ? "#10b981" : undefined)}
            </div>
          </div>
        );
      })()}

      {/* Frescura del catálogo CIMA (medications). No es una Source: hasta la §64
          sólo avisaba el email del watchdog y no se veía en ninguna pantalla. */}
      {catalog && (() => {
        const ok = catalog.status === "ok";
        const c = ok ? "#22c55e" : "#ef4444";
        return (
          <div style={{
            display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            background: "var(--clr-surface)", border: `1px solid ${ok ? "var(--clr-border)" : c + "55"}`,
            borderRadius: 18, padding: "0.85rem 1.2rem", marginBottom: "1.5rem",
            backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
            boxShadow: "var(--clr-shadow-sm)",
          }}>
            <StatusIcon status={ok ? "ok" : "warning"} size={10} />
            <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--clr-text)" }}>
              Catálogo CIMA
            </span>
            <span style={{ fontSize: "0.78rem", color: ok ? "var(--clr-text3)" : c }}>
              {ok
                ? `Actualizado hace ${catalog.days_since ?? 0} día(s)`
                : `Sin refrescar desde hace ${catalog.days_since ?? "?"} día(s) — revisar refresh_cima_catalog`}
            </span>
            <span style={{ marginLeft: "auto", fontSize: "0.74rem", color: "var(--clr-text4)" }}>
              {catalog.rows_refreshed.toLocaleString("es-ES")} / {catalog.rows_total.toLocaleString("es-ES")} presentaciones al día
            </span>
          </div>
        );
      })()}

      {/* KPIs: un panel de vidrio con franja de datos (números en tinta) */}
      <div style={{
        background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
        borderRadius: 18, padding: "1.1rem 1.4rem", marginBottom: "1.5rem",
        backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
        boxShadow: "var(--clr-shadow-sm)",
        display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))",
      }}>
        {[
          { label: "Fuentes",         value: sources.length },
          { label: "Activas",         value: sc["ok"] ?? 0,                                dot: "#22c55e" },
          { label: "Con avisos",      value: (sc["warning"] ?? 0) + (sc["pending"] ?? 0),  dot: "#f59e0b" },
          { label: "Inactivas",       value: sc["inactive"] ?? 0,                          dot: "#ef4444" },
          { label: "Cambios 7 días",  value: totalChanges7d },
          { label: "Cambios 30 días", value: totalChanges30d },
        ].map(({ label, value, dot }, i) => (
          <div key={label} style={{
            paddingLeft: i > 0 ? 18 : 0,
            borderLeft: i > 0 ? "1px solid var(--clr-border2)" : "none",
          }}>
            <div style={{ fontSize: "1.55rem", fontWeight: 700, letterSpacing: "-0.02em", color: dot || "var(--clr-text)", lineHeight: 1.05 }}>
              {value.toLocaleString("es-ES")}
            </div>
            <div style={{ fontSize: "0.64rem", color: "var(--clr-text4)", marginTop: 4, textTransform: "uppercase", letterSpacing: 0.8 }}>
              {label}
            </div>
          </div>
        ))}
      </div>

      {/* Filters + search */}
      <div style={{ display: "flex", gap: 8, marginBottom: "1.2rem", flexWrap: "wrap", alignItems: "center" }}>
        {([
          { key: "all",      label: `Todas (${sources.length})` },
          { key: "ok",       label: `Activas (${sc["ok"] ?? 0})` },
          { key: "warning",  label: `Avisos (${(sc["warning"] ?? 0) + (sc["pending"] ?? 0)})` },
          { key: "inactive", label: `Inactivas (${sc["inactive"] ?? 0})` },
        ] as const).map(tab => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            style={{
              padding: "0.42rem 1rem",
              borderRadius: 999,
              border: filter === tab.key ? "1px solid transparent" : "1px solid var(--clr-border)",
              background: filter === tab.key ? "var(--clr-org-primary)" : "var(--clr-surface)",
              color: filter === tab.key ? "#fff" : "var(--clr-text3)",
              fontWeight: filter === tab.key ? 600 : 500,
              fontSize: "0.78rem",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >{tab.label}</button>
        ))}

        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar fuente…"
          style={{
            marginLeft: "auto",
            padding: "0.42rem 1rem",
            border: "1px solid var(--clr-border)",
            borderRadius: 999,
            background: "var(--clr-input-bg)",
            color: "var(--clr-text)",
            fontSize: "0.78rem",
            outline: "none",
            width: 190,
          }}
        />
      </div>

      {/* Cards grid */}
      {filtered.length === 0 ? (
        <div style={{ textAlign: "center", padding: "3rem", color: "var(--clr-text4)", fontSize: "0.85rem" }}>
          No hay fuentes en esta categoría.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 14 }}>
          {filtered.map(s => (
            <SourceCard key={s.id} s={s} onClick={() => setSelected(s)} />
          ))}
        </div>
      )}

      {/* Table view below cards */}
      {filtered.length > 0 && (
        <div style={{ marginTop: "2rem" }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 600, color: "var(--clr-text4)", marginBottom: 10, textTransform: "uppercase", letterSpacing: 1 }}>
            Vista tabla
          </div>
          <div style={{
            background: "var(--clr-surface)", border: "1px solid var(--clr-border)",
            borderRadius: 18, overflow: "hidden",
            backdropFilter: "var(--clr-blur)", WebkitBackdropFilter: "var(--clr-blur)",
            boxShadow: "var(--clr-shadow-sm)",
          }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.8rem" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--clr-border2)" }}>
                  {["Fuente", "Estado", "Frecuencia", "7 días", "30 días", "Total", "Última actualización", "Próxima"].map(h => (
                    <th key={h} style={{
                      padding: "0.7rem 1rem", textAlign: "left",
                      fontSize: "0.65rem", fontWeight: 600,
                      textTransform: "uppercase", letterSpacing: 0.9,
                      color: "var(--clr-text4)", whiteSpace: "nowrap",
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => {
                  const isLast = i === filtered.length - 1;
                  return (
                    <tr
                      key={s.id}
                      onClick={() => setSelected(s)}
                      style={{
                        borderBottom: isLast ? "none" : "1px solid var(--clr-border2)",
                        cursor: "pointer",
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={e => (e.currentTarget as HTMLTableRowElement).style.background = "var(--clr-hover)"}
                      onMouseLeave={e => (e.currentTarget as HTMLTableRowElement).style.background = ""}
                    >
                      <td style={{ padding: "0.7rem 1rem" }}>
                        <div style={{ fontWeight: 600, color: "var(--clr-text)" }}>{s.name}</div>
                      </td>
                      <td style={{ padding: "0.7rem 1rem" }}>
                        <StatusPill status={s.status} />
                      </td>
                      <td style={{ padding: "0.7rem 1rem", color: "var(--clr-text3)" }}>{FREQ_LABEL[s.frequency] ?? s.frequency}</td>
                      <td style={{ padding: "0.7rem 1rem", fontWeight: 600, color: "var(--clr-text)" }}>{s.last_7d.toLocaleString("es-ES")}</td>
                      <td style={{ padding: "0.7rem 1rem", fontWeight: 600, color: "var(--clr-text)" }}>{s.last_30d.toLocaleString("es-ES")}</td>
                      <td style={{ padding: "0.7rem 1rem", color: "var(--clr-text2)" }}>{s.total.toLocaleString("es-ES")}</td>
                      <td style={{ padding: "0.7rem 1rem", color: "var(--clr-text3)", whiteSpace: "nowrap" }}>{fmtDate(s.last_successful_at)}</td>
                      <td style={{ padding: "0.7rem 1rem", color: "var(--clr-text4)", whiteSpace: "nowrap" }}>{s.next_run ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {selected && <SourceDetailModal s={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
